import axios from "axios";
import { formatDetail } from "./formatApiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Add Authorization header from localStorage as fallback (cookies are primary).
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("gf_session_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // RBAC Phase 2.2 — Block all mutations during Preview Mode.
  // The real Super Admin session is intact (nothing changes on the
  // server), so we short-circuit at the client with a synthetic 409.
  // Exception: the /rbac/preview/* endpoints themselves must succeed
  // so operators can start / stop preview.
  const method = (config.method || "get").toLowerCase();
  // Customer password sessions (HttpOnly cookie) require CSRF on mutations.
  if (!["get", "head", "options"].includes(method) && typeof document !== "undefined") {
    const m = document.cookie.match(/(?:^|;\s*)(?:__Host-gf_csrf|gf_csrf)=([^;]*)/);
    if (m) config.headers["X-CSRF-Token"] = decodeURIComponent(m[1]);
  }
  const preview = typeof window !== "undefined" ? window.__gfRbacPreview : null;
  if (preview && method !== "get" && method !== "head") {
    const url = (config.url || "");
    const isPreviewToggle = url.includes("/rbac/preview/");
    if (!isPreviewToggle) {
      const err = new Error("PREVIEW_MODE_BLOCKED");
      err.code = "PREVIEW_MODE_BLOCKED";
      err.previewRole = preview.role_name || preview.role_id;
      return Promise.reject(err);
    }
  }
  return config;
});

// RBAC Phase 2.1 — Silent 403 handling for background GET requests.
// A permission-restricted user visiting a page that fires several
// supplementary GETs would otherwise trigger dozens of dev-mode
// "Uncaught runtime error" overlays. Mutations still bubble 403 up.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    // Client-side preview block: convert to a friendly toast-friendly
    // rejection with a stable shape.
    if (error && error.code === "PREVIEW_MODE_BLOCKED") {
      return Promise.reject({
        response: {
          status: 409,
          data: {
            detail: `Preview mode active (viewing as ${error.previewRole}). ` +
                    "Exit preview to make changes.",
            code: "PREVIEW_MODE_BLOCKED",
          },
        },
        config: error.config,
        message: error.message,
      });
    }
    const status = error?.response?.status;
    const method = (error?.config?.method || "get").toLowerCase();
    if (status === 403 && method === "get") {
      return Promise.resolve({
        data: [],
        status: 403,
        headers: error.response.headers,
        config: error.config,
        _rbacDenied: true,
      });
    }
    // Phase 3E-9j — Normalise structured FastAPI validation errors so
    // downstream code that does `toast.error(err.response.data.detail)`
    // cannot receive an object/array and crash React with
    // "Objects are not valid as a React child". `detail` becomes a
    // human-readable string; the original array/object is preserved on
    // `detail_raw` for callers that need per-field access.
    try {
      const data = error?.response?.data;
      if (data && data.detail && typeof data.detail !== "string") {
        const asString = formatDetail(data.detail);
        if (asString) {
          data.detail_raw = data.detail;
          data.detail = asString;
        }
      }
    } catch (_) {
      // Never let normaliser bugs suppress the underlying error.
    }
    return Promise.reject(error);
  },
);

export default api;
